import {
    Box,
    Card,
    CardActions,
    CardContent,
    IconButton,
    ListItemText,
    Typography
} from "@mui/material";
import {
    Delete as DeleteIcon,
    Edit as EditIcon
} from '@mui/icons-material';

import ConfirmationPrompt from "../general/ConfirmationPrompt";
import { Contact } from "../../models";
import { ContactFormData } from "./ContactForm";
import { ContactFormDialog } from './ContactFormDialog';
import globalStyle from "../../assets/style/globalStyle";
import { graphQLContactDAO } from "../../lib/contact/dao/GraphQLContactDAO";
import { useQueryClient } from "react-query";
import { useState } from 'react';

type ContactCardProps = {
    contact: Contact;
};

export const ContactCard = (props: ContactCardProps) => {
    const { contact } = props;
    const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);

    const queryClient = useQueryClient();
    const globalStyles = globalStyle();

    const updateContact = async (formData: ContactFormData) => {
        await graphQLContactDAO.update({
            id: contact.id,
            name: formData.name,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            _version: (contact as any)._version
        });
        setIsEditDialogOpen(false);
        await queryClient.invalidateQueries("contacts");
    };

    return (
        <>
            <Card>
                <CardContent sx={{ pb: 0 }}>
                    <Box display="flex" flexDirection="column" gap={1}>
                        <Typography>
                            {contact.name}
                        </Typography>
                        {contact.email && (
                            <Typography variant="caption" color="textSecondary">
                                {contact.email}
                            </Typography>
                        )}
                        {contact.phoneNumber && (
                            <Typography variant="caption" color="textSecondary">
                                {contact.phoneNumber}
                            </Typography>
                        )}
                    </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: "flex-end", px: 2 }}>
                    <IconButton
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => setIsEditDialogOpen(true)}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => setIsDeleteDialogOpen(true)}
                    >
                        <DeleteIcon />
                    </IconButton>
                </CardActions>
            </Card>
            <ContactFormDialog
                isOpen={isEditDialogOpen}
                setIsOpen={setIsEditDialogOpen}
                type="edit"
                contact={contact}
                onSubmit={(formData: ContactFormData) => updateContact(formData)}
            />
            <ConfirmationPrompt
                promptTitle="Delete Contact"
                promptMessage={`Are you sure you want to delete ${contact.name}?`}
                onConfirmButtonClicked={async () => {
                    await graphQLContactDAO.delete(contact.id);
                    await queryClient.invalidateQueries("contacts");
                    setIsDeleteDialogOpen(false);
                }}
                isVisible={isDeleteDialogOpen}
                onCancelButtonClicked={() => setIsDeleteDialogOpen(false)}
            />
        </>
    );
};